// ============================================
// useAttorneyReview — React hook for Thomas
// General Counsel review session lifecycle.
// ============================================

import { useState, useCallback } from 'react';
import type {
  DocDocument, DocTemplate, BrandConfig, ReviewSession,
} from '../types/docops';
import { saveDocument } from '../services/firestore/docopsService';
import {
  createReviewSession,
  sendToThomas,
  generateFinalDocument,
} from '../services/agents/thomasReview';

export interface UseAttorneyReviewReturn {
  session: ReviewSession | null;
  isActive: boolean;
  isLoading: boolean;
  isGenerating: boolean;
  error: string | null;
  startReview: (doc: DocDocument, tpl: DocTemplate, config: BrandConfig) => Promise<void>;
  restartReview: (doc: DocDocument, tpl: DocTemplate, config: BrandConfig) => Promise<void>;
  sendMessage: (message: string, resolveIssue?: boolean) => Promise<void>;
  skipIssue: () => void;
  endReview: () => void;
  generateFinal: () => Promise<void>;
}

export function useAttorneyReview(
  brandId: string | null,
  onDocumentUpdated: (doc: DocDocument) => void,
): UseAttorneyReviewReturn {
  const [session, setSession] = useState<ReviewSession | null>(null);
  const [activeDoc, setActiveDoc] = useState<DocDocument | null>(null);
  const [activeConfig, setActiveConfig] = useState<BrandConfig | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isActive = session !== null;

  // Persist session to Firestore
  const persistSession = useCallback(async (doc: DocDocument, reviewSession: ReviewSession) => {
    if (!brandId) return;
    const updated: DocDocument = {
      ...doc,
      reviewSession,
      lastEditedAt: new Date().toISOString(),
      lastEditedBy: 'thomas-review',
    };
    try {
      await saveDocument(brandId, updated);
      setActiveDoc(updated);
      onDocumentUpdated(updated);
    } catch (err) {
      console.error('[Thomas] Failed to persist session:', err);
    }
  }, [brandId, onDocumentUpdated]);

  const startReview = useCallback(async (
    doc: DocDocument, tpl: DocTemplate, config: BrandConfig,
  ) => {
    setError(null);
    setIsLoading(true);
    setActiveDoc(doc);
    setActiveConfig(config);

    try {
      // Resume existing session if present
      if (doc.reviewSession && doc.reviewSession.phase !== 'complete') {
        setSession(doc.reviewSession);
        setIsLoading(false);
        return;
      }

      const { session: newSession } = await createReviewSession(doc, tpl, config);
      setSession(newSession);
      await persistSession(doc, newSession);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to start review';
      setError(msg);
      console.error('[Thomas] Start error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [persistSession]);

  // Force new session (clear persisted session and start fresh)
  const restartReview = useCallback(async (
    doc: DocDocument, tpl: DocTemplate, config: BrandConfig,
  ) => {
    setError(null);
    setIsLoading(true);
    setActiveDoc(doc);
    setActiveConfig(config);

    try {
      // Clear existing session from doc
      const cleared: DocDocument = { ...doc, reviewSession: undefined };
      if (brandId) {
        await saveDocument(brandId, cleared);
        onDocumentUpdated(cleared);
      }

      const { session: newSession } = await createReviewSession(cleared, tpl, config);
      setSession(newSession);
      await persistSession(cleared, newSession);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to restart review';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }, [brandId, persistSession, onDocumentUpdated]);

  const sendMessage = useCallback(async (message: string, resolveIssue = false) => {
    if (!session || !activeDoc || !activeConfig) return;
    setError(null);
    setIsLoading(true);

    try {
      const { updatedSession } = await sendToThomas(
        activeDoc, activeConfig, session, message, resolveIssue,
      );
      setSession(updatedSession);
      await persistSession(activeDoc, updatedSession);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to send message';
      setError(msg);
      console.error('[Thomas] Send error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [session, activeDoc, activeConfig, persistSession]);

  const skipIssue = useCallback(() => {
    if (!session) return;
    const updated = { ...session };
    let nextIdx = updated.currentIssueIndex + 1;
    while (nextIdx < updated.issues.length && updated.issues[nextIdx].resolved) {
      nextIdx++;
    }
    if (nextIdx >= updated.issues.length) {
      // Wrap around to find first unresolved
      nextIdx = updated.issues.findIndex(i => !i.resolved);
      if (nextIdx === -1) {
        updated.phase = 'consolidation';
      }
    }
    updated.currentIssueIndex = nextIdx >= 0 ? nextIdx : updated.currentIssueIndex;
    setSession(updated);
  }, [session]);

  const endReview = useCallback(() => {
    setSession(null);
    setActiveDoc(null);
    setActiveConfig(null);
    setError(null);
  }, []);

  const generateFinal = useCallback(async () => {
    if (!session || !activeDoc || !activeConfig || !brandId) return;
    setError(null);
    setIsGenerating(true);

    try {
      const finalContent = await generateFinalDocument(activeDoc, activeConfig, session);

      const completedSession: ReviewSession = {
        ...session,
        phase: 'complete',
        completedAt: new Date().toISOString(),
      };

      const updated: DocDocument = {
        ...activeDoc,
        content: finalContent,
        reviewSession: completedSession,
        version: activeDoc.version + 1,
        lastEditedAt: new Date().toISOString(),
        lastEditedBy: 'thomas-review',
      };

      await saveDocument(brandId, updated);
      setSession(completedSession);
      setActiveDoc(updated);
      onDocumentUpdated(updated);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to generate final document';
      setError(msg);
      console.error('[Thomas] Generate final error:', err);
    } finally {
      setIsGenerating(false);
    }
  }, [session, activeDoc, activeConfig, brandId, onDocumentUpdated]);

  return {
    session,
    isActive,
    isLoading,
    isGenerating,
    error,
    startReview,
    restartReview,
    sendMessage,
    skipIssue,
    endReview,
    generateFinal,
  };
}
